import { Box } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * The board is capped below its section and scrolls internally — the 24-hour column grid is
 * 4 320 px tall and would otherwise stretch the page — but it is free to be shorter than the
 * cap, so a two-lane resource board does not sit in a half-empty frame.
 */
const BOARD_MAX_HEIGHT = '70vh';

const VERTICAL_SCROLLBAR_WIDTH = 10;

/**
 * The horizontal bar is drawn by hand instead of being a styled browser scrollbar, because no
 * browser scrollbar can be made this thick: `::-webkit-scrollbar` does not exist in Firefox, and
 * `scrollbar-width` only chooses between `auto` and `thin`. The whole strip is the bar — no
 * padding around it — and it is there on touch screens too, where it is the easy way to move the
 * board sideways with a thumb (owner's call, 2026-09-16).
 */
const HORIZONTAL_BAR_HEIGHT = 12;

/** A wide board leaves a proportional thumb too small to grab; this is the floor. */
const MIN_THUMB_WIDTH = 48;

const scrollbarSkin = (thickness) => ({
  '&::-webkit-scrollbar': { width: `${thickness}px`, height: `${thickness}px` },
  '&::-webkit-scrollbar-track': { background: '#e8e8e8' },
  '&::-webkit-scrollbar-thumb': { background: '#9a9a9a', borderRadius: `${thickness / 2}px` },
  '&::-webkit-scrollbar-thumb:hover': { background: '#666' },
});

const frameSx = {
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  bgcolor: '#fff',
  mt: 2,
  mb: '48px',
  maxHeight: BOARD_MAX_HEIGHT,
  overflowX: 'hidden',
  overflowY: 'auto',
  // On a touch screen, reaching the end of the board must not carry on into the page, which
  // would take the header row off the screen with it. A mouse wheel still moves on to the page.
  '@media (hover: none)': { overscrollBehaviorY: 'contain' },
  ...scrollbarSkin(VERTICAL_SCROLLBAR_WIDTH),
};

// The bar sits above the header row, so the header sticks below it rather than at the frame's
// own top. The bar is there on every platform, so this offset always applies.
const headerSx = {
  position: 'sticky',
  top: `${HORIZONTAL_BAR_HEIGHT}px`,
  zIndex: 8,
  overflow: 'hidden',
};

const bodySx = {
  overflowX: 'auto',
  overflowY: 'hidden',
  // Past either end there is nothing to scroll sideways but the browser's back gesture.
  overscrollBehaviorX: 'contain',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

const barSx = {
  position: 'sticky',
  top: 0,
  zIndex: 8,
  height: `${HORIZONTAL_BAR_HEIGHT}px`,
  bgcolor: '#e8e8e8',
  overflow: 'hidden',
  cursor: 'pointer',
  // The bar takes every gesture that starts on it: a drag moves the board sideways instead of
  // panning the page or selecting the grid.
  touchAction: 'none',
  userSelect: 'none',
};

const thumbSx = {
  width: 0,
  height: '100%',
  borderRadius: `${HORIZONTAL_BAR_HEIGHT / 2}px`,
  bgcolor: '#9a9a9a',
  '&:hover': { bgcolor: '#666' },
};

/**
 * The scrolling frame of both dashboard boards: one axis per gesture, and a header row that stays
 * at the top.
 *
 * A single two-axis scroller lets a touch drift diagonally. This is two nested scrollers instead —
 * the frame runs vertically, `body` horizontally — and the browser hands each gesture to exactly
 * one of them. The price is that nothing can stick on both axes any more: the header row sticks to
 * the top of the frame, outside `body`, and follows `body`'s horizontal position from script. The
 * ruler or lane gutter inside `body` still sticks to the left on its own.
 *
 * `body`'s own scrollbar would sit under the last row of the grid, out of sight, so it is hidden
 * and the bar at the top of the frame drives the same scroll: the thumb's size and position are
 * `body`'s viewport and offset drawn to scale, and dragging it writes `body.scrollLeft` back.
 *
 * The ref reads and sets the position on both axes as if the board were one element: `scrollLeft`
 * is `body`'s, `scrollTop` the frame's.
 */
const ScheduleScroller = forwardRef(({ header, children }, ref) => {
  const frameRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const contentRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);

  useImperativeHandle(ref, () => ({
    get scrollLeft() {
      return bodyRef.current.scrollLeft;
    },
    set scrollLeft(value) {
      bodyRef.current.scrollLeft = value;
    },
    get scrollTop() {
      return frameRef.current.scrollTop;
    },
    set scrollTop(value) {
      frameRef.current.scrollTop = value;
    },
  }), []);

  useEffect(() => {
    const body = bodyRef.current;
    const headerRow = headerRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;

    // How far `body` can scroll, and how far the thumb can travel for it. Both are recomputed
    // whenever the board or the window changes size, never per frame of a drag.
    let maxScroll = 0;
    let maxThumbLeft = 0;

    const drawThumb = () => {
      const progress = maxScroll > 0 ? body.scrollLeft / maxScroll : 0;
      thumb.style.transform = `translateX(${progress * maxThumbLeft}px)`;
    };

    const measure = () => {
      const trackWidth = track.clientWidth;
      // Nothing to scroll: a full-width thumb says so by itself, and keeping the bar in place
      // keeps the header row's sticky offset honest.
      const visibleRatio = body.scrollWidth > 0 ? Math.min(body.clientWidth / body.scrollWidth, 1) : 1;
      const thumbWidth = Math.min(Math.max(visibleRatio * trackWidth, MIN_THUMB_WIDTH), trackWidth);

      thumb.style.width = `${thumbWidth}px`;
      maxScroll = Math.max(body.scrollWidth - body.clientWidth, 0);
      maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
      drawThumb();
    };

    const onBodyScroll = () => {
      headerRow.scrollLeft = body.scrollLeft;
      drawThumb();
    };

    // Writing `body.scrollLeft` is what moves everything: its scroll event redraws the thumb and
    // carries the header row along, so a drag never positions the thumb directly.
    const scrollToThumbLeft = (left) => {
      const clamped = Math.min(Math.max(left, 0), maxThumbLeft);
      body.scrollLeft = maxThumbLeft > 0 ? (clamped / maxThumbLeft) * maxScroll : 0;
    };

    // Where in the thumb the pointer took hold, so the thumb does not jump under the finger.
    // A press on the bare track grabs the thumb by its middle and brings it to the pointer.
    let grabOffset = null;

    const onPointerDown = (event) => {
      if (event.button !== 0) return;

      const thumbBox = thumb.getBoundingClientRect();
      const onThumb = event.clientX >= thumbBox.left && event.clientX <= thumbBox.right;
      grabOffset = onThumb ? event.clientX - thumbBox.left : thumbBox.width / 2;

      track.setPointerCapture(event.pointerId);
      scrollToThumbLeft(event.clientX - track.getBoundingClientRect().left - grabOffset);
      event.preventDefault();
    };

    const onPointerMove = (event) => {
      if (grabOffset === null) return;
      scrollToThumbLeft(event.clientX - track.getBoundingClientRect().left - grabOffset);
    };

    const endDrag = (event) => {
      grabOffset = null;
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    };

    measure();
    headerRow.scrollLeft = body.scrollLeft;

    body.addEventListener('scroll', onBodyScroll, { passive: true });
    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // More days, a collapsed gutter or a narrower window all change how far `body` scrolls.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(body);
    observer?.observe(contentRef.current);

    return () => {
      body.removeEventListener('scroll', onBodyScroll);
      track.removeEventListener('pointerdown', onPointerDown);
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', endDrag);
      track.removeEventListener('pointercancel', endDrag);
      observer?.disconnect();
    };
  }, []);

  return (
    <Box ref={frameRef} sx={frameSx} data-testid="schedule-scroller">
      <Box ref={trackRef} sx={barSx}>
        <Box ref={thumbRef} sx={thumbSx} />
      </Box>
      <Box ref={headerRef} sx={headerSx}>
        <Box sx={{ minWidth: 'max-content' }}>{header}</Box>
      </Box>
      <Box ref={bodyRef} sx={bodySx} data-testid="schedule-scroller-body">
        <Box ref={contentRef} sx={{ minWidth: 'max-content' }}>{children}</Box>
      </Box>
    </Box>
  );
});

export default ScheduleScroller;
